import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { EasmScanConfigurationSchedulerSchema, WeekDays } from '../types';
import { RecurrenceOptions } from '../../../../types/common/schedulerTypes';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { translateFn } from '../../../../utils/translator.helper';
import moment from 'moment-timezone';
import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { GzMultiSelectGroupWebComponent } from '../../../../ui/gzMultiSelect';
import { GzTimePickerWebComponent } from '../../../../ui/gzTimePicker';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { htmlEncode } from '../../../../utils/escape.utils';

export class EasmScanConfigurationSchedulerView extends BaseWebComponentView {
  public formGroup!: FormGroup;

  public dom: {
    $wrapper: HTMLElement;

    $schedulerTitle: HTMLElement;

    $recurrenceFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    // Weekly
    $recurrenceWeeklyOptionsContainer: HTMLElement;
    // On the following day(s) - setup day selector
    $weeklyOnFormField: GzFormFieldWebComponent<GzMultiSelectGroupWebComponent, Array<string | number>>;

    $commonOptionsContainer: HTMLElement;
    $betweenLabeledContent: GzTextWebComponent;
    $betweenStartFormField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
    $lastScanLabel: GzTextWebComponent;
    $nextScanLabel: GzTextWebComponent;
    $lastScanValue: GzTextWebComponent;
    $nextScanValue: GzTextWebComponent;
  };

  public getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $schedulerTitle: this.shadow.querySelector('#scheduler-title'),
      $recurrenceFormField: this.shadow.querySelector('#recurrence-form-field'),
      // Weekly
      $recurrenceWeeklyOptionsContainer: this.shadow.querySelector('#recurrence-weekly-container'),
      // On the following day(s) - setup day selector
      $weeklyOnFormField: this.shadow.querySelector('#weekly-on-form-field'),

      $commonOptionsContainer: this.shadow.querySelector('#common-fields-container'),
      $betweenLabeledContent: this.shadow.querySelector('#start-time-label'),
      $betweenStartFormField: this.shadow.querySelector('#start-time-form-field'),
      $lastScanLabel: this.shadow.querySelector('#last-scan-date-label'),
      $nextScanLabel: this.shadow.querySelector('#next-scan-label'),
      $lastScanValue: this.shadow.querySelector('#last-scan-value'),
      $nextScanValue: this.shadow.querySelector('#next-scan-value'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public getTranslations(): void {
    this.translations = this.stringsTranslator.getSchedulerStrings();
  }

  public setWeekDaysValue(weekDay: WeekDays): void {
    this.dom.$weeklyOnFormField.Input.setFormValue([weekDay]);
  }

  public setTranslatedStrings(): void {
    this.dom.$recurrenceFormField.label = htmlEncode(this.dependencies.translateFn('Recurrence'));
    this.dom.$lastScanLabel.innerText = htmlEncode(this.dependencies.translateFn('Last scan date'));
    this.dom.$nextScanLabel.innerText = htmlEncode(this.dependencies.translateFn('Next scheduled scan'));
    // Weekly - On the following day(s)
    this.dom.$weeklyOnFormField.label = htmlEncode(this.dependencies.translateFn('On the following day'));
    this.dom.$betweenLabeledContent.innerText = htmlEncode(this.dependencies.translateFn('Start time'));
  }

  public buildSuccessView(data: {
    viewData: EasmScanConfigurationSchedulerSchema;
    disabled: boolean;
    hasImmediatelyOption: boolean;
  }): void {
    this.createFormGroup();
    this.setTitle();
    this.populateDropdownsOptions();
    this.populateDayPicker();

    this.setInitialDataInForm(data.viewData);
  }

  private populateDayPicker(): void {
    const daysArray = Object.keys(WeekDays).filter(x => isNaN(Number(x)));
    const options = Array.from(daysArray, element => ({
      label: this.translations.weekdays.shortNames[element],
      value: parseInt(WeekDays[element], 10),
    }));

    this.dom.$weeklyOnFormField.Input.setAttribute('options', JSON.stringify(options));
  }

  private populateDropdownsOptions(): void {
    this.populateRecurrenceDropdown();
  }

  private populateRecurrenceDropdown(): void {
    const recurrenceOptions: Array<any> = [];

    recurrenceOptions.push({
      id: 1,
      title: this.translations.recurrence.WEEKLY,
      value: String(RecurrenceOptions.WEEKLY),
    });

    this.dom.$recurrenceFormField.Input.setAttribute('options', JSON.stringify(recurrenceOptions));
  }

  private createFormGroup(): void {
    this.dom.$recurrenceFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$weeklyOnFormField.Validators = [
      RequiredValidator(this.dependencies.translateFn('Select a day of the week.')),
    ];

    // create FormGroup
    this.formGroup = new FormGroup({
      recurrence: this.dom.$recurrenceFormField,
      weeklyRecurrence: new FormGroup({
        day: this.dom.$weeklyOnFormField,
      }),
      start: this.dom.$betweenStartFormField,
    });
  }

  private setInitialDataInForm(viewData: EasmScanConfigurationSchedulerSchema): void {
    this.dom.$nextScanValue.innerText =
      viewData.nextScheduledScan && viewData.isActive
        ? moment(viewData.nextScheduledScan).format('D MMM YYYY, HH:mm')
        : '---';
    this.dom.$lastScanValue.innerText = viewData.lastScanDate
      ? moment(viewData.lastScanDate).format('D MMM YYYY, HH:mm')
      : '---';

    let followingDay = viewData.weeklyRecurrence?.day;
    if (followingDay === 7) {
      followingDay = 0;
    }

    this.formGroup.value = {
      recurrence: RecurrenceOptions.WEEKLY.toString(),
      weeklyRecurrence: {
        day: viewData.weeklyRecurrence?.day !== undefined ? [followingDay] : [],
      },
      start: {
        hours: viewData.start.hours ?? 0,
        minutes: viewData.start.minutes ?? 0,
      },
    };
  }

  public getFormGroup(): FormGroup {
    return this.formGroup;
  }

  private setTitle(): void {
    this.dom.$schedulerTitle.innerText = translateFn('Scheduler');
  }
}
